import { Link, Navigate, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  AuthDivider,
  AuthErrorAlert,
  AuthInput,
  AuthLayout,
  AuthSubmitButton,
  CaptchaWidget,
  OAuthButton,
  PasswordRequirements,
  isCaptchaConfigured,
} from '@/features/auth/components'
import { useOAuthSignIn, useSignUp } from '@/features/auth/hooks'
import { validatePassword } from '@/features/auth/utils/validation'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'
import { useSession } from '@/lib/auth-client'

export const Route = createFileRoute('/sign-up')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/sign-up']),
    links: generateLinks(routeSEO['/sign-up']),
  }),
  component: SignUpPage,
})

const LINK_CLASS =
  'font-semibold text-primary underline-offset-4 hover:underline'

function SignUpPage() {
  const { data: session, isPending: isSessionPending } = useSession()
  // Read query params to redirect after sign-up
  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null
  const refParam = searchParams?.get('ref') || null
  const continueParam =
    searchParams?.get('continue') || searchParams?.get('redirect') || null

  const redirectAfterAuth = continueParam
    ? continueParam
    : refParam
      ? `/dashboard?ref=${encodeURIComponent(refParam)}`
      : '/dashboard'

  const { signUpWithEmail, isLoading, error } = useSignUp(redirectAfterAuth)
  const { signInWithOAuth, isLoading: isGoogleLoading } = useOAuthSignIn()
  const [password, setPassword] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string>()

  const { requirements } = validatePassword(password)

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)

    await signUpWithEmail({
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      termsAccepted: formData.get('terms') === 'on',
      referralCode: refParam ?? undefined,
      captchaToken,
    })
  }

  const handleGoogleSignIn = () => {
    signInWithOAuth('google', redirectAfterAuth)
  }

  if (isSessionPending) return null
  if (session?.user) return <Navigate to={redirectAfterAuth} />

  return (
    <AuthLayout
      endpoint="/api/auth/sign-up/email"
      title="Create your account"
      description="Start building with unified AI model access"
      footer={
        <>
          Already have an account?{' '}
          <Link
            to="/sign-in"
            search={continueParam ? { continue: continueParam } : {}}
            className={LINK_CLASS}
          >
            Sign in
          </Link>
        </>
      }
    >
      <AuthErrorAlert error={error} />

      <OAuthButton
        provider="google"
        onClick={handleGoogleSignIn}
        isLoading={isGoogleLoading}
        disabled={isLoading}
      />

      <AuthDivider text="Or sign up with email" />

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthInput
          type="text"
          id="name"
          name="name"
          label="Full Name"
          placeholder="John Doe"
          icon="person"
          required
          disabled={isLoading}
        />

        <AuthInput
          type="email"
          id="email"
          name="email"
          label="Email"
          placeholder="you@company.com"
          icon="mail"
          required
          disabled={isLoading}
        />

        <div>
          <AuthInput
            type="password"
            id="password"
            name="password"
            label="Password"
            placeholder="Create a strong password"
            icon="lock"
            required
            disabled={isLoading}
            showPasswordToggle
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {password && (
            <div className="mt-3">
              <PasswordRequirements requirements={requirements} />
            </div>
          )}
        </div>

        <div className="flex items-start gap-2.5">
          <Checkbox
            id="terms"
            name="terms"
            required
            className="mt-0.5 accent-primary"
          />
          <Label
            htmlFor="terms"
            className="font-normal leading-snug text-[var(--color-text-secondary)]"
          >
            I agree to the{' '}
            <Link to="/terms" className={LINK_CLASS}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className={LINK_CLASS}>
              Privacy Policy
            </Link>
          </Label>
        </div>

        {/* Bot/spam protection */}
        <CaptchaWidget
          onVerify={setCaptchaToken}
          onExpire={() => setCaptchaToken(undefined)}
        />

        <AuthSubmitButton
          isLoading={isLoading}
          disabled={isGoogleLoading || (isCaptchaConfigured && !captchaToken)}
        >
          Create Account
        </AuthSubmitButton>
      </form>
    </AuthLayout>
  )
}
